import axios, { type AxiosError } from 'axios'

import useAuth from '@/composables/auth/useAuth'
import { addErrorMessages } from '@/composables/feedback/notification/useErrorMessageModal'
import { type CustomAxiosConfig } from '@/types/axios'

import { baseURL } from './base'

const armSecureAxios = axios.create({
  baseURL
})

export const handleUnauthorizedError = async (error: AxiosError<any, any>): Promise<never> => {
  const originalRequest = error.config as CustomAxiosConfig
  const unauthorizedErrorEnabled = originalRequest.unauthorizedErrorEnabled ?? true

  if (error.response?.status === 401 && unauthorizedErrorEnabled) {
    addErrorMessages({ type: 'unauthorized' })
  }

  return await Promise.reject(error)
}

export const handleUnknownError = async (error: AxiosError<any, any>): Promise<never> => {
  if (error.response?.status === undefined && !navigator.onLine) {
    addErrorMessages({ type: 'no-internet' })
  }

  return await Promise.reject(error)
}

export const handleRequestError = async (error: AxiosError<any, any>): Promise<never> => {
  const originalRequest = error.config as CustomAxiosConfig
  const requestErrorStatusIgnores = originalRequest.requestErrorStatusIgnores ?? []

  if (originalRequest.requestErrorEnabled &&
      error.response?.status !== undefined &&
      error.response.status !== 401 && !requestErrorStatusIgnores.includes(error.response.status)
  ) {
    addErrorMessages({
      type: 'request',
      status: error.response.status
    })
  }

  return await Promise.reject(error)
}

armSecureAxios.interceptors.response.use((response) => response, handleUnauthorizedError)
armSecureAxios.interceptors.response.use((response) => response, handleUnknownError)
armSecureAxios.interceptors.response.use((response) => response, handleRequestError)

armSecureAxios.interceptors.request.use(async (config) => {
  const auth = useAuth()
  const accessToken = auth.getAccessToken()
  if (auth.loggedIn && accessToken !== null) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  return config
})

export default armSecureAxios
